<template>
  <div>
    <v-row dense>
      <v-col :order="this.$vuetify.breakpoint.smAndDown ? 'last' : 'first'" cols="12" md="4">
        <v-btn color="primary" small text @click="downloadChart">
          <v-icon left>mdi-download</v-icon>
          {{ $t('actions.download') }}
        </v-btn>
      </v-col>
      <v-col :class="this.$vuetify.breakpoint.smAndDown ? '' : 'text-right'" :order="this.$vuetify.breakpoint.smAndDown ? 'first' : 'last'" cols="12"
             md="8">
        <v-menu
            ref="menu"
            v-model="chart.range.opened"
            :close-on-content-click="false"
            min-width="290px"
            transition="scale-transition"
        >
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-btn small text>
                <v-icon left>mdi-calendar</v-icon>
                {{ $moment(chart.range.date[0]).format('ll') }}
                {{ $t('to') }}
                {{ $moment(chart.range.date[1]).format('ll') }}
              </v-btn>
            </span>
          </template>
          <v-date-picker v-model="chart.range.date" no-title range scrollable @change="refresh">
            <v-list dense width="100%">
              <v-list-item-group color="primary">
                <v-list-item @click="changeRange($moment().subtract(30, 'day'), $moment())">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ $t('30-last-days') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item @click="changeRange($moment().subtract(60, 'day'), $moment())">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ $t('60-last-days') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item @click="changeRange($moment().subtract(90, 'day'), $moment())">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ $t('90-last-days') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item @click="changeRange($moment().startOf('year'), $moment())">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ $t('this-year') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                    @click="changeRange($moment().subtract(1, 'year').startOf('year'), $moment().subtract(1, 'year').endOf('year'))">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ $t('last-year') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-date-picker>
        </v-menu>
      </v-col>
    </v-row>

    <div style="min-height: 400px;">
      <div v-show="chart.instance === null" style="padding: 20px;">
        <v-skeleton-loader height="200" type="image"></v-skeleton-loader>
      </div>
      <div v-show="chart.instance !== null">
        <canvas id="monitor-history-chart" style="padding: 15px;"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import {Chart, Filler, LinearScale, LineController, LineElement, PointElement, TimeScale, Tooltip} from 'chart.js'
import moment from 'moment'
import 'chartjs-adapter-moment'
import DOMPurify from 'dompurify'

Chart.register(LinearScale, LineController, PointElement, LineElement, TimeScale, Filler, Tooltip)

const skipped = (ctx, value) => ctx.p0.skip || ctx.p1.skip ? value : undefined;

export default {
  name: 'KeywordEvolutionChart',
  props: ['monitor'],
  data() {
    return {
      history: null,
      chart: {
        instance: null,
        range: {
          date: [this.$moment().subtract(this.getStartInDays(), 'day').format('YYYY-MM-DD'), this.$moment().format('YYYY-MM-DD')],
          opened: false
        },
        params: {
          data: {
            datasets: [{
              label: this.$t('position'),
              data: [],
              fill: {
                target: 'origin',
                below: 'rgba(25, 118, 210, 0.1)'
              },
              segment: {
                borderDash: ctx => skipped(ctx, [6, 6]),
              },
              borderWidth: 2,
              pointBackgroundColor: '#FFFFFF',
              pointBorderWidth: ({raw}) => {
                return this.getMomentNotes(raw.x).length > 0 ? 3 : 2
              },
              radius: ({raw}) => {
                return this.getMomentNotes(raw.x).length > 0 ? 4 : 0
              },
              borderColor: ({raw}) => {
                if (raw) {
                  return this.getMomentNotes(raw.x).length > 0 ? '#FB8C00' : '#1976D2'
                } else {
                  return '#1976D2'
                }
              },
              pointStyle: 'circle',
              spanGaps: true
            }],
          },
          options: {
            aspectRatio: () => {
              if (this.$vuetify.breakpoint.smAndUp) return 2
              else return 1
            },
            resizeDelay: 100,
            interaction: {
              intersect: false,
              mode: 'index',
            },
            scales: {
              x: {
                type: 'time',
                time: {
                  unit: 'day'
                },
                ticks: {
                  stepSize: 1,
                  min: 1,
                  reverse: true,
                }
              },
              y: {
                reverse: true,
                min: 1,
              }
            },
            plugins: {
              tooltip: {
                enabled: false,
                external: (context) => {
                  // Tooltip Element
                  var tooltipEl = document.getElementById('chartjs-tooltip')

                  // Create element on first render
                  if (!tooltipEl) {
                    tooltipEl = document.createElement('div')
                    tooltipEl.id = 'chartjs-tooltip'
                    document.body.appendChild(tooltipEl)
                  }

                  // Hide if no tooltip
                  var tooltipModel = context.tooltip
                  if (tooltipModel.opacity === 0) {
                    tooltipEl.style.opacity = 0
                    return
                  }

                  tooltipEl.innerHTML = '<strong>' +
                      moment(context.tooltip.dataPoints[0].raw.x).format('ll') + '</strong><br />' +
                      context.tooltip.body[0].lines[0]

                  const notes = this.getMomentNotes(context.tooltip.dataPoints[0].raw.x)
                  if (notes.length > 0) tooltipEl.innerHTML += '<div style="margin: 10px;"></div><strong>Notes</strong><br />'
                  notes.forEach(note => {
                    tooltipEl.innerHTML += '- ' + DOMPurify.sanitize(note.description) + '<br />'
                  })

                  var position = context.chart.canvas.getBoundingClientRect()

                  // Display, position, and set styles for font
                  tooltipEl.style.opacity = 1
                  tooltipEl.style.zIndex = 1000
                  tooltipEl.style.background = '#ffffff'
                  tooltipEl.style.padding = '6px'
                  tooltipEl.style.maxWidth = '50%'
                  tooltipEl.style.borderRadius = '5px'
                  tooltipEl.style.border = '1px solid #ddd'
                  tooltipEl.style.position = 'absolute'
                  tooltipEl.style.color = 'rgba(0, 0, 0, 0.6)'
                  tooltipEl.style.fontFamily = 'Roboto, sans-serif'
                  tooltipEl.style.fontSize = '14px'
                  tooltipEl.style.fontWeight = '400px'
                  tooltipEl.style.lineHeight = '22px'
                  tooltipEl.style.letterSpacing = '0.1px'
                  tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 10 + 'px'
                  tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 20 + 'px'
                  tooltipEl.style.pointerEvents = 'none'
                }
              }
            }
          },
        }
      },
      carrier: {},
    }
  },
  methods: {
    downloadChart() {
      const a = document.createElement('a')
      a.href = this.chart.instance.toBase64Image()
      a.download = 'chart.png'
      a.click()
    },
    changeRange(start, end) {
      this.chart.range.date = [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')]
      this.chart.range.opened = false
      this.refresh()
    },
    showChart(data) {
      this.chart.params.data.datasets[0].data = data
      if (this.chart.instance === null) {
        const ctx = document.getElementById('monitor-history-chart')
        this.chart.instance = new Chart(ctx, {
          type: 'line',
          data: this.chart.params.data,
          options: this.chart.params.options,
        })
      } else {
        this.chart.instance.data = this.chart.params.data
        this.chart.instance.update()
      }
    },
    getMomentNotes(date) {
      if (this.monitor && this.monitor.notes) {
        return this.monitor.notes.filter(note => {
          return this.$moment(note.at).isSame(date, 'day')
        })
      } else {
        return []
      }
    },
    getStartInDays() {
      const ageInDays = this.$helpers.monitors.getAgeInDays(this.monitor)
      return ageInDays > 360 ? 360 : ageInDays
    },
    async getDailyRangeHistory(start, end) {
      const days = {}
      const years = {}
      while (start <= end) {
        years[moment(start).format('YYYY')] = true
        days[moment(start).format('YYYY-MM-DD')] = {
          date: moment(start).clone(),
          name: moment(start).format('DD/MM/YYYY'),
          value: null,
        }
        start = moment(start).add(1, 'days')
      }

      const values = await this.gatherYearPositions(['2021', '2022', '2023', '2024', '2025', '2026'])

      for (let day in days) {
        const year = days[day].date.format('YYYY')
        const dayOfYear = days[day].date.format('DDD')
        const value = values[year][dayOfYear]
        if (value) days[day].value = value
      }

      return days
    },
    async gatherYearPositions(years) {
      const positions = {}

      for (let i = 0; i < years.length; i++) {
        try {
          const path = this.$models.CarrierObjects.monitorHistory(this.monitor, years[i])
          positions[years[i]] = (await this.$carrier.get(path)).data
        } catch (e) {
          if (e.code === 404) {
            positions[years[i]] = {year: years[i], data: {}}
          }
        }
      }

      return positions
    },
    async refresh() {
      const days = await this.getDailyRangeHistory(
          this.$moment(this.chart.range.date[0]),
          this.$moment(this.chart.range.date[1])
      )

      const data = []
      for (let day in days) {
        data.push({
          x: days[day].date,
          y: days[day].value,
        })
      }

      this.showChart(data)
    }
  },
  created() {
    this.refresh()
  }
}
</script>

<style scoped>

</style>

<i18n>
{
  "en": {
    "30-last-days": "The last 30 days",
    "60-last-days": "The last 60 days",
    "90-last-days": "The last 90 days",
    "last-year": "Last year",
    "position": "Position",
    "this-year": "This year",
    "to": "to"
  },
  "es": {
    "30-last-days": "Los últimos 30 días",
    "60-last-days": "Los últimos 60 días",
    "90-last-days": "Los ultimos 90 dias",
    "last-year": "El año pasado",
    "position": "Posición",
    "this-year": "Este año",
    "to": "hasta"
  },
  "fr": {
    "30-last-days": "Les 30 derniers jours",
    "60-last-days": "Les 60 derniers jours",
    "90-last-days": "Les 90 derniers jours",
    "last-year": "L'année dernière",
    "position": "Position",
    "this-year": "Cette année",
    "to": "au"
  },
  "it": {
    "30-last-days": "Gli ultimi 30 giorni",
    "60-last-days": "Gli ultimi 60 giorni",
    "90-last-days": "Gli ultimi 90 giorni",
    "last-year": "L'anno scorso",
    "position": "Posizione",
    "this-year": "Quest'anno",
    "to": "al"
  },
  "pt": {
    "30-last-days": "Os últimos 30 dias",
    "60-last-days": "Os últimos 60 dias",
    "90-last-days": "Os últimos 90 dias",
    "last-year": "Ano passado",
    "position": "Posição",
    "this-year": "Este ano",
    "to": "até"
  },
  "pl": {
    "30-last-days": "Ostatnie 30 dni",
    "60-last-days": "Ostatnie 60 dni",
    "90-last-days": "Ostatnie 90 dni",
    "last-year": "Poprzedni rok",
    "position": "Pozycja",
    "this-year": "Ten rok",
    "to": "do"
  }
}
</i18n>

